/**
 * Write-Up Form PDF Generator
 * Generates a printable write-up document for employee signatures.
 */
import { format } from 'date-fns';
import { THRESHOLDS, POINT_TYPES, getActivePoints, calculateTotalPoints, getWarningLevel } from './attendancePoints';

/**
 * Generate a write-up form as a new browser window/print dialog
 */
export function generateWriteUpPdf({
  employee,
  totalPoints,
  warningLevel,
  pointHistory,
  managerName,
  additionalNotes,
}) {
  const now = new Date();
  const activePoints = getActivePoints(pointHistory);
  const infractions = activePoints.filter(p => p.points > 0 && !p.voided);

  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Employee Write-Up - ${employee.firstName} ${employee.lastName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; padding: 40px; font-size: 13px; line-height: 1.6; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1a2634; padding-bottom: 16px; }
    .header h1 { font-size: 22px; color: #1a2634; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 1px; }
    .header h2 { font-size: 14px; color: #64748b; font-weight: 400; }
    .company-name { font-size: 16px; font-weight: 600; color: #1a2634; margin-bottom: 4px; }
    .section { margin-bottom: 24px; }
    .section-title { font-size: 14px; font-weight: 700; color: #1a2634; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
    .info-item { display: flex; gap: 8px; }
    .info-label { font-weight: 600; color: #475569; min-width: 140px; }
    .info-value { color: #1a1a1a; }
    .warning-box { padding: 14px 18px; border: 2px solid; border-radius: 6px; margin-bottom: 16px; text-align: center; }
    .warning-box.verbal_coaching { border-color: #f59e0b; background: #fffbeb; }
    .warning-box.no_bonus { border-color: #f97316; background: #fff7ed; }
    .warning-box.written_warning { border-color: #ef4444; background: #fef2f2; }
    .warning-box.final_warning { border-color: #dc2626; background: #fef2f2; }
    .warning-box.termination { border-color: #991b1b; background: #fef2f2; }
    .warning-box strong { font-size: 16px; display: block; margin-bottom: 4px; }
    .points-summary { font-size: 20px; font-weight: 700; text-align: center; margin: 12px 0; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th, td { padding: 8px 10px; text-align: left; border: 1px solid #e2e8f0; font-size: 12px; }
    th { background: #f8fafc; font-weight: 600; color: #334155; }
    td.pts { text-align: center; font-weight: 700; }
    .notes-section { min-height: 100px; border: 1px solid #e2e8f0; border-radius: 4px; padding: 12px; margin-bottom: 16px; white-space: pre-wrap; }
    .notes-section.empty { color: #94a3b8; font-style: italic; min-height: 80px; }
    .signature-section { margin-top: 40px; }
    .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 20px; }
    .sig-block { }
    .sig-line { border-bottom: 1px solid #1a1a1a; height: 40px; margin-bottom: 4px; }
    .sig-label { font-size: 11px; color: #64748b; }
    .disclaimer { font-size: 10px; color: #94a3b8; margin-top: 24px; line-height: 1.5; border-top: 1px solid #e2e8f0; padding-top: 12px; }
    .threshold-ref { display: flex; gap: 0; margin-bottom: 16px; border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
    .threshold-ref-item { flex: 1; text-align: center; padding: 6px 4px; border-right: 1px solid #e2e8f0; font-size: 10px; }
    .threshold-ref-item:last-child { border-right: none; }
    .threshold-ref-item .t-pts { font-weight: 700; font-size: 13px; display: block; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">Two Sisters Cleaning & Organizing</div>
    <h1>Employee Write-Up Form</h1>
    <h2>Attendance Point System Documentation</h2>
  </div>

  <div class="section">
    <div class="section-title">Employee Information</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Employee Name:</span>
        <span class="info-value">${employee.firstName} ${employee.lastName}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Date:</span>
        <span class="info-value">${format(now, 'MMMM d, yyyy')}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Position:</span>
        <span class="info-value">${employee.role || 'Team Member'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Hire Date:</span>
        <span class="info-value">${employee.hireDate ? format(new Date(employee.hireDate), 'MMMM d, yyyy') : 'N/A'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Manager:</span>
        <span class="info-value">${managerName || '________________________'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Email:</span>
        <span class="info-value">${employee.email || 'N/A'}</span>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Discipline Level</div>
    <div class="warning-box ${warningLevel?.level || ''}">
      <strong>${warningLevel?.label || 'Attendance Documentation'}</strong>
      Current Total: ${totalPoints.toFixed(2)} points
    </div>
    <div class="threshold-ref">
      ${THRESHOLDS.map(t => `
        <div class="threshold-ref-item">
          <span class="t-pts">${t.points}</span>
          ${t.label}
        </div>
      `).join('')}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Attendance Infractions (90-Day Window)</div>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Type</th>
          <th>Description</th>
          <th>Scheduled</th>
          <th>Actual</th>
          <th style="text-align:center">Points</th>
        </tr>
      </thead>
      <tbody>
        ${infractions.length > 0 ? infractions.map(p => `
          <tr>
            <td>${p.date ? format(new Date(p.date), 'M/d/yy') : 'N/A'}</td>
            <td>${POINT_TYPES[p.type]?.label || p.type}</td>
            <td>${p.description || ''}${p.managerNote ? ' (' + p.managerNote + ')' : ''}</td>
            <td>${p.scheduledTime || '-'}</td>
            <td>${p.actualTime ? format(new Date(p.actualTime), 'h:mm a') : '-'}</td>
            <td class="pts">+${p.points}</td>
          </tr>
        `).join('') : '<tr><td colspan="6" style="text-align:center;color:#94a3b8;">No infractions recorded</td></tr>'}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="5" style="text-align:right;font-weight:700;">Total Active Points:</td>
          <td class="pts" style="font-size:14px;">${totalPoints.toFixed(2)}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Manager Notes / Action Plan</div>
    <div class="notes-section ${additionalNotes ? '' : 'empty'}">
      ${additionalNotes || 'Manager: Document specific expectations, corrective actions required, and timeline for improvement here.'}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Employee Acknowledgment</div>
    <p style="margin-bottom:8px;">
      By signing below, I acknowledge that I have received and reviewed this write-up. I understand the
      attendance expectations and the consequences of further infractions as outlined in the attendance point system.
      My signature does not necessarily indicate agreement, but confirms that I have been informed of the above.
    </p>
    <p style="margin-bottom:12px;">
      <strong>I understand that reaching 8 points within a 90-day period may result in termination of employment.</strong>
    </p>

    <div class="signature-section">
      <div class="sig-grid">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-label">Employee Signature</div>
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-label">Date</div>
        </div>
      </div>
      <div class="sig-grid" style="margin-top:24px;">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-label">Manager Signature</div>
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-label">Date</div>
        </div>
      </div>
    </div>
  </div>

  <div class="disclaimer">
    This document is part of the employee's personnel file. A copy should be provided to the employee.
    Points are calculated on a 90-day rolling basis and expire automatically after 90 days.
    This write-up was generated by the Two Sisters Cleaning & Organizing attendance tracking system on ${format(now, 'M/d/yyyy \'at\' h:mm a')}.
  </div>
</body>
</html>
  `;

  return html;
}

/**
 * Open write-up in a new window for printing/saving as PDF
 */
export function openWriteUpForPrint(params) {
  const html = generateWriteUpPdf(params);
  const printWindow = window.open('', '_blank', 'width=800,height=1100');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    // Slight delay to let styles render before print dialog
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }
  return html;
}
